import axios from 'axios';
import { LoginFormData, AuthResponse, User } from '../types/auth';
import { Equipment, EquipmentFormData, EquipmentCreateData, EquipmentFilters } from '../types/equipment';
import { Rental, RentalFormData, RentalFilters, PaginatedResponse, FeeDetailsFilters, FeeDetailsRow } from '../types/rental';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// 分塊上傳配置
const CHUNK_SIZE = 300 * 1024; // 300KB per chunk
const MAX_RETRIES = 3;

// 分塊上傳輔助函數
const uploadPhotosInChunks = async (photos: File[], licensePlate: string) => {
  const token = localStorage.getItem('token');
  const photoPaths: string[] = [];
  
  for (let photoIndex = 0; photoIndex < photos.length; photoIndex++) {
    const photo = photos[photoIndex];
    const totalChunks = Math.ceil(photo.size / CHUNK_SIZE);
    
    console.log(`上傳照片 ${photoIndex + 1}/${photos.length}: ${photo.name}, 分塊數: ${totalChunks}`);
    
    // 上傳每個分塊
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, photo.size);
      const chunk = photo.slice(start, end);
      
      let retries = 0;
      let success = false;
      
      while (retries < MAX_RETRIES && !success) {
        try {
          const formData = new FormData();
          formData.append('file', chunk);
          formData.append('photo_index', photoIndex.toString());
          formData.append('chunk_index', chunkIndex.toString());
          formData.append('total_chunks', totalChunks.toString());
          formData.append('file_name', photo.name);
        formData.append('file_size', photo.size.toString());
        formData.append('license_plate', licensePlate);
          
          const response = await api.post('/photos/vehicle/upload-chunk/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            }
          });
          
          console.log(`分塊 ${chunkIndex + 1}/${totalChunks} 響應:`, response.data);
          
                 if (response.data.success) {
                   success = true;
                   console.log(`分塊 ${chunkIndex + 1}/${totalChunks} 上傳成功`);
                   
                   // 如果是最後一個分塊，保存照片路徑
                   if (chunkIndex === totalChunks - 1 && response.data.photo_paths) {
                     photoPaths.push(...response.data.photo_paths);
                   }
                 } else {
                   throw new Error(response.data.message || '分塊上傳失敗');
                 }
        } catch (error: any) {
          retries++;
          console.error(`分塊 ${chunkIndex + 1} 上傳失敗 (嘗試 ${retries}/${MAX_RETRIES}):`, error);
          
          if (retries >= MAX_RETRIES) {
            throw new Error(`照片 ${photo.name} 分塊 ${chunkIndex + 1} 上傳失敗: ${error.message}`);
          }
          
          // 等待後重試
          await new Promise(resolve => setTimeout(resolve, 1000 * retries));
        }
      }
    }
  }
  
  return {
    success: true,
    message: `成功上傳 ${photos.length} 張照片`,
    photo_paths: photoPaths
  };
};

// 關聯照片到車輛的函數
const linkPhotosToVehicle = async (vehicleId: number, photoPaths: string[]) => {
  const token = localStorage.getItem('token');
  
  const response = await api.post('/photos/vehicle/link/', {
    vehicle_id: vehicleId,
    photo_paths: photoPaths
  }, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  return response.data;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 請求攔截器 - 添加認證token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 響應攔截器 - 處理認證錯誤
let isRedirecting = false; // 防止重複跳轉

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 處理 401 和 403 認證錯誤
    if (error.response?.status === 401 || error.response?.status === 403) {
      // 檢查是否為登入請求
      const isLoginRequest = error.config?.url?.includes('/auth/login/');
      
      if (isLoginRequest) {
        // 登入請求的錯誤，不自動跳轉，讓登入頁面處理
        return Promise.reject(error);
      } else {
        // 防止重複跳轉
        if (!isRedirecting) {
          isRedirecting = true;
          alert('登入已過期，請重新登入');
          localStorage.removeItem('token');
          localStorage.removeItem('refresh');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// 認證相關API
export const authAPI = {
  login: async (credentials: LoginFormData): Promise<AuthResponse> => {
    const response = await api.post('/auth/login/', credentials);
    return response.data;
  },

  getProfile: async (token: string): Promise<User> => {
    const response = await api.get('/auth/profile/', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  refreshToken: async (): Promise<{ access: string }> => {
    const refresh = localStorage.getItem('refresh');
    const response = await api.post('/auth/refresh/', { refresh });
    return response.data;
  },
};


// 機具相關API
export const equipmentAPI = {
  getEquipment: async (filters?: EquipmentFilters): Promise<Equipment[]> => {
    const response = await api.get('/equipment/', { params: filters });
    const data = response.data;
    // 處理分頁格式的響應
    return Array.isArray(data) ? data : data.results || [];
  },

  getEquipmentById: async (id: number): Promise<Equipment> => {
    const response = await api.get(`/equipment/${id}/`);
    return response.data;
  },

  createEquipment: async (data: EquipmentCreateData): Promise<Equipment> => {
    // 檢查是否有照片
    const photos = data.photos || [];
    
    if (photos.length > 0) {
      // 如果有照片，先上傳照片，再創建設備
      const photoUploadResults = await uploadPhotosInChunks(photos, data.license_plate);
      
      if (!photoUploadResults.success) {
        throw new Error(photoUploadResults.message);
      }
      
      // 照片上傳成功後，創建設備（後端會自動關聯照片）
      const { photos: _, ...equipmentData } = data;
      const paths = photoUploadResults.photo_paths || [];
      const payload = {
        ...equipmentData,
        photo_path_1: paths[0] ?? '',
        photo_path_2: paths[1] ?? '',
        photo_path_3: paths[2] ?? '',
        photo_path_4: paths[3] ?? '',
      };
      const response = await api.post('/equipment/', payload);
      const newEquipment = response.data;

      return newEquipment;
    } else {
      // 沒有照片，使用普通 JSON
      const response = await api.post('/equipment/', data);
      return response.data;
    }
  },

  updateEquipment: async (id: number, data: Partial<EquipmentFormData> & { photos?: File[] }): Promise<Equipment> => {
    // 如果有照片，使用 FormData
    if (data.photos && data.photos.length > 0) {
      const formData = new FormData();
      
      // 添加基本資料
      if (data.name !== undefined) formData.append('name', data.name);
      if (data.license_plate !== undefined) formData.append('license_plate', data.license_plate);
      if (data.mileage !== undefined) formData.append('mileage', String(data.mileage));
      if (data.status !== undefined) formData.append('status', data.status);
      if (data.info !== undefined) formData.append('info', data.info);
      
      // 添加照片
      data.photos.forEach((photo) => {
        formData.append('photos', photo);
      });
      
      const response = await api.put(`/equipment/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      // 沒有照片，使用普通 JSON
      const { photos, ...equipmentData } = data;
      const response = await api.put(`/equipment/${id}/`, equipmentData);
      return response.data;
    }
  },

  deleteEquipment: async (id: number): Promise<void> => {
    await api.delete(`/equipment/${id}/`);
  },

  // 照片上傳 API
  uploadVehiclePhotos: async (licensePlate: string, photos: File[]): Promise<{ message: string; uploaded_paths: string[]; count: number }> => {
    const formData = new FormData();
    formData.append('license_plate', licensePlate);
    photos.forEach(photo => {
      formData.append('photos', photo);
    });
    
    const response = await api.post('/photos/vehicle/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  uploadEquipmentPhotos: async (equipmentCode: string, photos: File[]): Promise<{ message: string; uploaded_paths: string[]; count: number }> => {
    const formData = new FormData();
    formData.append('equipment_code', equipmentCode);
    photos.forEach(photo => {
      formData.append('photos', photo);
    });
    
    const response = await api.post('/photos/equipment/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  uploadPhoto: async (equipmentId: number, file: File, photoType: string): Promise<any> => {
    const formData = new FormData();
    formData.append('equipment_id', equipmentId.toString());
    formData.append('photo', file);
    formData.append('photo_type', photoType);

    const response = await api.post('/equipment/photos/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
};

// 租借相關API
export const rentalAPI = {
  getRentals: async (filters?: RentalFilters): Promise<PaginatedResponse<Rental>> => {
    const response = await api.get('/rentals/management/', { params: filters });
    return response.data;
  },

  // 費用明細（使用 rental_usage 的起始/結束時間；後端固定 status='completed'）
  getFeeDetails: async (filters?: FeeDetailsFilters): Promise<PaginatedResponse<FeeDetailsRow>> => {
    const response = await api.get('/rentals/management/fee_details/', { params: filters });
    return response.data;
  },

  // 讀取照片為 Blob 並回傳臨時 URL（授權由 axios 帶 JWT）
  getPhotoBlobUrl: async (photoPath: string): Promise<string> => {
    // 注意：photoPath 是資料庫相對路徑（例如 equipment_rental/records/...）
    // 這裡透過後端的 /api/vehicle/photo/<path> 來讀取任意 NAS 照片
    const response = await api.get(`/vehicle/photo/${encodeURIComponent(photoPath)}`, {
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    return URL.createObjectURL(blob);
  },

  getUsage: async (id: number): Promise<{ rental_id: number; usage_records: any[]; photos: string[] }> => {
    const response = await api.get(`/rentals/management/${id}/usage/`);
    return response.data;
  },

  getStatistics: async (): Promise<{ total: number; reserved: number; ready: number; active: number; completed: number; cancelled: number }> => {
    const response = await api.get('/rentals/management/statistics/');
    return response.data;
  },

  getRentalById: async (id: number): Promise<Rental> => {
    const response = await api.get(`/rentals/management/${id}/`);
    return response.data;
  },

  createRental: async (data: RentalFormData): Promise<Rental> => {
    const response = await api.post('/rentals/management/', data);
    return response.data;
  },

  updateRental: async (id: number, data: Partial<RentalFormData>): Promise<Rental> => {
    const response = await api.put(`/rentals/management/${id}/`, data);
    return response.data;
  },

  approveRental: async (id: number): Promise<Rental> => {
    const response = await api.post(`/rentals/management/${id}/approve/`);
    return response.data;
  },

  cancelRental: async (id: number): Promise<Rental> => {
    const response = await api.post(`/rentals/management/${id}/cancel/`);
    return response.data;
  },

  returnEquipment: async (id: number): Promise<Rental> => {
    const response = await api.post(`/rentals/management/${id}/return/`);
    return response.data;
  },

  completeRental: async (id: number): Promise<{ message: string; is_early_completion?: boolean; scheduled_end_date?: string; actual_end_date?: string }> => {
    const response = await api.post(`/rentals/management/${id}/complete/`);
    return response.data;
  },

  importRecord: async (data: FormData): Promise<Rental> => {
    const response = await api.post('/rentals/management/import_record/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  updateWithUsage: async (id: number, data: FormData): Promise<Rental> => {
    const response = await api.put(`/rentals/management/${id}/update_with_usage/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteRental: async (id: number): Promise<void> => {
    await api.delete(`/rentals/management/${id}/`);
  },
};

// 公告相關API
export const announcementAPI = {
  getAnnouncements: async (): Promise<any> => {
    const response = await api.get('/announcements/');
    return response.data;
  },

  createAnnouncement: async (data: any): Promise<any> => {
    const response = await api.post('/announcements/create_announcement/', data);
    return response.data;
  },

  getHistory: async (): Promise<any> => {
    const response = await api.get('/announcements/history/');
    return response.data;
  },
};

export default api;

// 通用 API（部門等）
export interface DepartmentOption { id: number; name: string }

export interface DepartmentUserOption {
  id: number;
  username: string;
  name: string;
  department_id: number;
  department_name?: string;
}

export const commonAPI = {
  getDepartments: async (): Promise<DepartmentOption[]> => {
    const response = await api.get('/auth/departments/');
    return response.data;
  },

  getDepartmentUsers: async (departmentId: number): Promise<DepartmentUserOption[]> => {
    const response = await api.get('/auth/department-users/', {
      params: { department_id: departmentId },
    });
    return response.data;
  },
};

// 帳號相關（註冊和申請）
export interface RegisterPayload {
  username: string;
  name: string;
  password: string;
  email: string;
  department: number; // 部門 id
}

export interface ApplyAccountPayload {
  username: string;
  name: string;
  password: string;
  email: string;
  department_id: number;
  status?: string;
  datetime?: string;
}

export interface Application {
  id: number;
  username: string;
  name: string;
  email: string;
  department_id: number;
  department_name?: string;
  status: 'pending' | 'approved' | 'rejected';
  datetime: string;
}

// 人員資料類型
export interface Personnel {
  id: number;
  username: string;
  name: string;
  email: string;
  department_id: number;
  department_name?: string;
  is_active: boolean;
  permission: number;
}

// 人員表單資料類型
export interface PersonnelFormData {
  username: string;
  name: string;
  email: string;
  department_id: number;
  is_active: boolean;
  permission: number;
}

export const accountAPI = {
  register: async (data: RegisterPayload): Promise<{ message: string }> => {
    const response = await api.post('/auth/register/', data);
    return response.data;
  },
  
  apply: async (data: ApplyAccountPayload): Promise<{ message: string; application_id: number }> => {
    const response = await api.post('/auth/apply/', data);
    return response.data;
  },

  getApplications: async (): Promise<Application[]> => {
    const response = await api.get('/admin/applications/');
    return response.data;
  },

  approveApplication: async (applicationId: number): Promise<{ message: string }> => {
    const response = await api.post(`/admin/applications/${applicationId}/approve/`);
    return response.data;
  },

  rejectApplication: async (applicationId: number, reason: string): Promise<{ message: string }> => {
    const response = await api.post(`/admin/applications/${applicationId}/reject/`, { notes: reason });
    return response.data;
  },

  // 人員管理相關 API
  getPersonnel: async (): Promise<Personnel[]> => {
    const response = await api.get('/auth/personnel/');
    return response.data;
  },

  createPersonnel: async (data: PersonnelFormData): Promise<{ message: string }> => {
    const response = await api.post('/auth/personnel/create/', data);
    return response.data;
  },

  updatePersonnel: async (personnelId: number, data: PersonnelFormData): Promise<{ message: string }> => {
    const response = await api.put(`/auth/personnel/${personnelId}/update/`, data);
    return response.data;
  },

  deletePersonnel: async (personnelId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/auth/personnel/${personnelId}/delete/`);
    return response.data;
  },

  resetPersonnelPassword: async (personnelId: number, newPassword: string): Promise<{ message: string }> => {
    const response = await api.post(`/auth/personnel/${personnelId}/reset-password/`, { new_password: newPassword });
    return response.data;
  },
};

// 車輛專用 API（打 /vehicles/）
export const vehicleAPI = {
  getVehicles: async (filters?: EquipmentFilters): Promise<Equipment[]> => {
    const response = await api.get('/vehicles/', { params: filters });
    const data = response.data;
    return Array.isArray(data) ? data : data.results || [];
  },

  getVehicleById: async (id: number): Promise<Equipment> => {
    const response = await api.get(`/vehicles/${id}/`);
    return response.data;
  },

  createVehicle: async (data: EquipmentCreateData): Promise<Equipment> => {
    // 與 createEquipment 相同邏輯：若含照片先分塊上傳，再送出建立請求
    const photos = data.photos || [];
    if (photos.length > 0) {
      const photoUploadResults = await uploadPhotosInChunks(photos, data.license_plate);
      if (!photoUploadResults.success) {
        throw new Error(photoUploadResults.message);
      }
      const { photos: _, ...vehicleData } = data;
      const response = await api.post('/vehicles/', vehicleData);
      return response.data;
    } else {
      const response = await api.post('/vehicles/', data);
      return response.data;
    }
  },

  updateVehicle: async (id: number, data: Partial<EquipmentFormData> & { photos?: File[]; photo_positions?: number[]; keep_photo_urls?: (string | null)[] }): Promise<Equipment> => {
    // 總是使用 FormData（因為需要發送 keep_photo_urls）
    const formData = new FormData();
    if (data.name !== undefined) formData.append('name', data.name);
    if (data.license_plate !== undefined) formData.append('license_plate', data.license_plate);
    if (data.mileage !== undefined) formData.append('mileage', String(data.mileage));
    if (data.status !== undefined) formData.append('status', data.status);
    if (data.info !== undefined) formData.append('info', data.info);
    
    // 添加新上傳的照片及其位置
    if (data.photos && data.photos.length > 0) {
      data.photos.forEach((photo, index) => {
        formData.append('photos', photo);
        // 添加照片對應的位置
        if (data.photo_positions && data.photo_positions[index] !== undefined) {
          formData.append('photo_positions[]', String(data.photo_positions[index]));
        }
      });
    }
    
    // 添加保留的照片 URL（4 個位置）
    if (data.keep_photo_urls) {
      data.keep_photo_urls.forEach((url) => {
        formData.append('keep_photo_urls[]', url || '');
      });
    }
    
    const response = await api.put(`/vehicles/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteVehicle: async (id: number): Promise<void> => {
    await api.delete(`/vehicles/${id}/`);
  },
};

export interface VehiclePriceRow {
  id: number | null;
  vehicle_id: number;
  hourly_rate: number | string | null;
  half_day_threshold_hours: number | string | null;
  extra_mileage_threshold_km: number | string | null;
  extra_mileage_rate_per_km: number | string | null;
  half_day_without_driver_rate: number | string | null;
  half_day_with_driver_rate: number | string | null;
  full_day_without_driver_rate: number | string | null;
  full_day_with_driver_rate: number | string | null;
  is_active: boolean;
  updated_at?: string | null;
}

export interface VehiclePriceHistoryRow {
  id: number;
  vehicle_id: number;
  hourly_rate: number | string | null;
  half_day_threshold_hours: number | string | null;
  extra_mileage_threshold_km: number | string | null;
  extra_mileage_rate_per_km: number | string | null;
  half_day_without_driver_rate: number | string | null;
  half_day_with_driver_rate: number | string | null;
  full_day_without_driver_rate: number | string | null;
  full_day_with_driver_rate: number | string | null;
  created_at: string;
}

export const vehiclePriceAPI = {
  get: async (vehicleId: number): Promise<VehiclePriceRow> => {
    const response = await api.get(`/vehicles/${vehicleId}/vehicle_price/`);
    return response.data;
  },
  put: async (
    vehicleId: number,
    data: Omit<VehiclePriceRow, 'id' | 'vehicle_id' | 'updated_at'>
  ): Promise<VehiclePriceRow> => {
    const response = await api.put(`/vehicles/${vehicleId}/vehicle_price/`, data);
    return response.data;
  },
  history: async (vehicleId: number): Promise<VehiclePriceHistoryRow[]> => {
    const response = await api.get(`/vehicles/${vehicleId}/vehicle_price/history/`);
    return response.data;
  },
};

export interface EquipmentPriceRow {
  id: number | null;
  equipment_id: number;
  day_rate: number | string | null;
  hourly_rate: number | string | null;
  is_active: boolean;
  updated_at?: string | null;
}

export interface EquipmentPriceHistoryRow {
  id: number;
  equipment_id: number;
  day_rate: number | string | null;
  hourly_rate: number | string | null;
  created_at: string;
}

export const equipmentPriceAPI = {
  get: async (equipmentId: number): Promise<EquipmentPriceRow> => {
    const response = await api.get(`/equipment/${equipmentId}/equipment_price/`);
    return response.data;
  },
  put: async (
    equipmentId: number,
    data: Pick<EquipmentPriceRow, 'day_rate' | 'hourly_rate'> & { is_active?: boolean }
  ): Promise<EquipmentPriceRow> => {
    const response = await api.put(`/equipment/${equipmentId}/equipment_price/`, data);
    return response.data;
  },
  history: async (equipmentId: number): Promise<EquipmentPriceHistoryRow[]> => {
    const response = await api.get(`/equipment/${equipmentId}/equipment_price/history/`);
    return response.data;
  },
};
